<script>
import Alert from "@/components/Alert";
import Banks from "@/components/Banks";
import Button from "@/components/Button";
import ProductItem from "@/components/CheckoutProduct/Item";
import ProductList from "@/components/CheckoutProduct/List";
import ClientData from "@/components/ClientData";
import BooleanField from "@/components/Form/BooleanField";
import fieldValidator from "@/helper/fieldValidator";
import __ from "@/helper/translation";

import { currency, settings, termsHeading, termsLink } from "@/stores/basket";
import { checkout } from "@/stores/checkout";
import { clientData, saveClientData } from "@/stores/clientData";
import { clientDataFields } from "@/stores/clientDataFields";
import {
  products,
  productsTotalVat,
  productsTotalVatPrice,
} from "@/stores/product";
import { transportTotalVat, transportTotalVatPrice, isSelectedCarrierParcelMachine, selectedCountryId } from "@/stores/transport";

import { setStep } from "@/stores/view";
import _forEach from "lodash/forEach";
import _values from "lodash/values";
import { computed, reactive, ref, watch } from "vue";
import {
  formatPrice,
  roundPrice,
  vatPrice,
} from "../../../../../../Greativ/helper/price";
import isDelfiPhoneNumber from "../../helper/delfi";

export default {
  components: {
    Alert,
    Button,
    BooleanField,
    ProductList,
    ProductItem,
    Banks,
    ClientData,
  },
  setup() {
    const loading = ref(false);

    const errorMessage = ref("");

    const askPhoneNumberConfirmation = computed(() => {
      const phoneNumber = String(clientData.value?.phone || "");

      return !!(phoneNumber &&
          !phoneNumber.replace(/ /g, "").trim().startsWith("+372") &&
          isSelectedCarrierParcelMachine.value &&
          parseInt(selectedCountryId.value) === 69);
    });

    const terms = reactive({
      agreeTerms: {
        value: null,
        error: false,
      },
      privacyPolicy: {
        value: null,
        error: false,
      },
      conductAndSecurity: {
        value: null,
        error: false,
      },
      notValidParcelMachinePhoneNumber: {
        value: null,
        error: false,
      },
    });

    watch(
      terms,
      (terms) => {
        if (terms.agreeTerms.value) {
          terms.agreeTerms.error = false;
        }

        if (terms.privacyPolicy.value) {
          terms.privacyPolicy.error = false;
        }

        if (terms.conductAndSecurity.value) {
          terms.conductAndSecurity.error = false;
        }

        if (terms.notValidParcelMachinePhoneNumber.value) {
          terms.notValidParcelMachinePhoneNumber.error = false;
        }
      },
      {
        deep: true,
      }
    );

    const showDelfiPopup = ref(false)
    const triggerDelfiPopup = async () => {
      showDelfiPopup.value = true
    }

    const delfiLoading = ref(false)
    async function dontWantDelfi(){
      delfiLoading.value = true
      const { success, redirecting, message } = await checkout();
      if (success) {
        if (!redirecting) {
          setStep(3);
        }
      } else {
         errorMessage.value = message || "";
         loading.value = false;
         showDelfiPopup.value = false
      }
    }

    async function wantDelfi(){
      delfiLoading.value = false
      showDelfiPopup.value = false
    }

    function askDelfi(){
      return clientData.value.wantDelfi !== '1' &&
      clientData.value.wantDelfi !== true &&
      isDelfiPhoneNumber(clientData.value.phone) ?
      true :
      false
    }

    const goToPay = async () => {
      loading.value = true;
      errorMessage.value = "";

      let anyFieldError = false;

      _forEach(clientDataFields.value, (field) => {
        const isSuccess = fieldValidator(field, clientDataFields.value);
        if (!isSuccess) {
          anyFieldError = true;
        }
      });

      if (!terms.agreeTerms.value) {
        terms.agreeTerms.error = true;
        anyFieldError = true;
      }

      if (!terms.privacyPolicy.value) {
        terms.privacyPolicy.error = true;
        anyFieldError = true;
      }

      if (!terms.conductAndSecurity.value) {
        terms.conductAndSecurity.error = true;
        anyFieldError = true;
      }

      if (askPhoneNumberConfirmation.value && !terms.notValidParcelMachinePhoneNumber.value) {
        terms.notValidParcelMachinePhoneNumber.error = true;
        anyFieldError = true;
      }

      if (!anyFieldError) {
        await saveClientData();

        if(askDelfi()){
          await triggerDelfiPopup()
          loading.value = false;
          return
         }

        const { success, message, redirecting } = await checkout();

        if (!success) {
          errorMessage.value = message || "";
          loading.value = false;
        }

        if (success) {
          if (!redirecting) {
            setStep(3);
          }
        }
      } else {
        errorMessage.value = __("check_inserted_data");
        loading.value = false;
      }
    };

    const privacyPolicyAgreementLink =
      window.shop_basket.privacy_policy_agreement_link;
    const conductAndSecurityAgreementLink =
      window.shop_basket.conduct_and_security_agreement_link;

    const filteredProducts = computed(() => {
      if (settings.value.partner_data) {
        const collectProducts = {};

        _forEach([...products.value], (product) => {
          let customPrice = 0;

          if (product.extraDiscount !== null) {
            customPrice = roundPrice(
              vatPrice(product.extraDiscount, product.vat) * product.amount
            );
          } else if (product.discountPrice) {
            customPrice = roundPrice(
              vatPrice(product.discountPrice, product.vat) * product.amount
            );
          } else {
            customPrice = roundPrice(
              vatPrice(product.price, product.vat) * product.amount
            );
          }

          if (!collectProducts[product.productName]) {
            collectProducts[product.productName] = {
              ...product,
              customPrice,
            };
          } else {
            collectProducts[product.productName].amount =
              collectProducts[product.productName].amount + product.amount;
            collectProducts[product.productName].customPrice =
              collectProducts[product.productName].customPrice + customPrice;
          }
        });

        return _values(collectProducts);
      }

      return products.value;
    });

    return {
      formatPrice,
      currency,
      filteredProducts,
      productsTotalVatPrice,
      productsTotalVat,
      transportTotalVatPrice,
      transportTotalVat,
      setStep,
      loading,
      goToPay,
      errorMessage,
      termsHeading,
      termsLink,
      terms,
      privacyPolicyAgreementLink,
      conductAndSecurityAgreementLink,
      settings,
      askPhoneNumberConfirmation,
      showDelfiPopup,
      delfiLoading,
      wantDelfi,
      dontWantDelfi
    };
  },
};
</script>

<template>
  <teleport to="body">
    <Transition
      name="tw-custom-fade"
      mode="out-in"
      enterActiveClass="transition-opacity duration-300"
      enterFromClass="opacity-0"
      leaveActiveClass="transition-opacity duration-300"
      leaveToClass="opacity-0"
    >
      <div
        class="z-100 fixed inset-0 bg-black bg-opacity-70"
        v-if="showDelfiPopup"
      ></div>
    </Transition>
    <Transition
      name="tw-custom-fade"
      mode="out-in"
      enterActiveClass="transition-opacity duration-300"
      enterFromClass="opacity-0"
      leaveActiveClass="transition-opacity duration-300"
      leaveToClass="opacity-0"
    >
      <div class="fixed inset-0 overflow-y-auto z-101" v-if="showDelfiPopup">
        <div class="flex min-h-full items-center justify-center p-[1.6rem]">
          <div class="bg-white p-12">
            <h3 class="mb-8 !text-[2.5rem]">{{ __('delfi.title') }}</h3>
            <div class="flex flex-wrap justify-end gap-[1.6rem] mt-10">
              <div class="w-full sm:!w-auto">
                <Button
                  class="w-full sm:!w-auto"
                  styleType="secondary"
                  :disabled="delfiLoading"
                  @click="wantDelfi()"
                  >{{ __('delfi.btnNo') }}
                </Button>
              </div>
              <div class="w-full sm:!w-auto">
                <Button
                  class="w-full sm:!w-auto"
                  :disabled="delfiLoading"
                  :loading="delfiLoading"
                  @click="dontWantDelfi()"
                  >{{ __('delfi.btnYes') }}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </teleport>

  <div class="flex flex-col lg:flex-row">
    <div class="flex-column mb-18 flex flex-1 lg:mr-18 lg:mb-0">
      <h3 class="with-bg">{{ __("client_information_title") }}</h3>

      <ClientData :locked="loading" />

      <Alert
        class="lg:mb-14"
        v-if="settings && settings.has_credit && settings.partner_data"
      >
        {{
          __("send_partner_replay_email", {
            email: settings.partner_data.email,
          })
        }}
      </Alert>

      <div class="mt-auto hidden lg:block">
        <Button styleType="secondary" :disabled="loading" @click="setStep(1)">
          {{ __("btn_change_order") }}
        </Button>
      </div>
    </div>

    <div class="flex-shrink-0 lg:w-5/12 lg:pl-16">
      <ProductList>
        <ProductItem
          v-for="product in filteredProducts"
          :key="product.uID"
          :product="product"
        />
      </ProductList>

      <Banks :locked="loading" />

      <div class="mb-20 space-y-6">
        <div v-if="termsHeading && termsLink">
          <BooleanField
            id="terms"
            :label="termsHeading"
            value="1"
            :link="termsLink"
            :required="true"
            :error="terms.agreeTerms.error"
            variant="light"
            v-model="terms.agreeTerms.value"
          />
        </div>
        <div>
          <BooleanField
            id="privacy"
            :label="__('privacy_policy_agreement')"
            value="1"
            :link="privacyPolicyAgreementLink"
            :required="true"
            :error="terms.privacyPolicy.error"
            variant="light"
            v-model="terms.privacyPolicy.value"
          />
        </div>

        <div>
          <BooleanField
            id="conduct_and_security"
            :label="__('conduct_and_security_agreement')"
            value="1"
            :link="conductAndSecurityAgreementLink"
            :required="true"
            :error="terms.conductAndSecurity.error"
            variant="light"
            v-model="terms.conductAndSecurity.value"
          />
        </div>

        <div v-if="askPhoneNumberConfirmation">
          <Alert>
            {{
              __("inserted_phone_number_not_valid_send_parcel_machine_code_msg")
            }}

            <div class="mt-8">
              <BooleanField
                id="conduct_and_security"
                :label="
                  __(
                    'inserted_phone_number_not_valid_send_parcel_machine_agree_text'
                  )
                "
                value="1"
                :required="true"
                :error="terms.notValidParcelMachinePhoneNumber.error"
                variant="light"
                v-model="terms.notValidParcelMachinePhoneNumber.value"
              />
            </div>
          </Alert>
        </div>
      </div>

      <template v-if="settings && settings.has_credit && settings.partner_data">
        <div
          class="flex flex-wrap items-center justify-between text-3xl font-bold uppercase text-red"
          v-html="
            __('used_credit_title', {
              price: `<span>${formatPrice(
                settings.partner_data.used_credit_in_basket
              )} ${currency}</span>`,
            })
          "
        ></div>

        <div
          class="mb-3 flex flex-wrap items-center justify-between text-3xl font-bold uppercase text-red"
          v-html="
            __('surcharge_title', {
              price: `<span>${formatPrice(
                productsTotalVatPrice + transportTotalVatPrice
              )} ${currency}</span>`,
            })
          "
          v-if="productsTotalVatPrice + transportTotalVatPrice > 0"
        ></div>

        <div
          class="mb-3 flex flex-wrap items-center justify-between text-3xl font-black uppercase text-red"
          v-html="
            __('total_price_title', {
              price: `<span>${formatPrice(
                settings.partner_data.used_credit_in_basket +
                  productsTotalVatPrice +
                  transportTotalVatPrice
              )} ${currency}</span>`,
            })
          "
        ></div>
      </template>

      <div
        class="flex flex-wrap items-center justify-between text-4xl font-black uppercase text-red"
        v-else
      >
        <div>{{ __("total_title") }}:</div>
        <div class="ml-6 text-right">
          {{ formatPrice(productsTotalVatPrice + transportTotalVatPrice) }}
          {{ currency }}
        </div>
        <div
          class="w-full text-right text-7 font-medium normal-case text-black"
        >
          ({{
            __("including_vat_price_title", {
              price: `${formatPrice(
                productsTotalVat + transportTotalVat
              )} ${currency}`,
            })
          }})
        </div>
      </div>

      <Alert v-if="errorMessage" class="mt-12 -mb-8">
        {{ errorMessage }}
      </Alert>

      <Button class="btn-block mt-14" :loading="loading" @click="goToPay">
        {{
          productsTotalVatPrice + transportTotalVatPrice > 0
            ? __("btn_pay")
            : __("btn_next")
        }}
      </Button>

      <div class="mt-8 lg:hidden">
        <Button
          class="btn-block"
          styleType="secondary"
          :disabled="loading"
          @click="setStep(1)"
        >
          {{ __("btn_change_order") }}
        </Button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
h3.with-bg {
  position: relative;
  color: #fff;
  text-transform: uppercase;
  padding: 1.5rem;
  margin-bottom: 4rem;
  overflow: hidden;
  z-index: 1;

  @media only screen and (max-width: 640px) {
    font-size: 2.5rem;
  }

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: -1.4rem;
    right: 1.4rem;
    bottom: 0;
    background: #dd1d20;
    z-index: -1;
    transform: skew(-25deg);
  }
}
</style>
